/**
 * Delivery channels, ported from the prototype's CHANNELS table. `validate(address)` returns ""
 * or a message that says how to fix the address. Only email, Slack and Teams send today (LIVE_CHANNELS);
 * the others are listed so the UI can show them disabled with a "Coming soon" tag.
 */
import { LIVE_CHANNELS, TEAMS_WEBHOOK_HOSTS, TEAMS_WEBHOOK_MAX_LENGTH, type ChannelApi } from "./distributions.types";
import { EXAMPLE_EMAIL, EXAMPLE_SLACK_CHANNEL } from "./distributionExamples";

export interface DistributionChannelConfig {
  id: ChannelApi;
  label: string;
  /** Icon key for `PlatformIcon`-style lookups. */
  icon: "mail" | "slack" | "teams" | "lock" | "drive";
  placeholder: string;
  hint: string;
  comingSoon: boolean;
  validate: (address: string) => string;
}

export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;
/** As magic-core: an email address is at most 254 characters. */
const EMAIL_MAX_LENGTH = 254;
const SLACK_NAME_PATTERN = /^#[a-z0-9][a-z0-9._-]*$/i;
/** A Slack channel id such as C0123ABCD. */
/** As magic-core SLACK_ID_PATTERN: C or G, then 8 to 12 letters or digits. */
const SLACK_ID_PATTERN = /^[CG][A-Z0-9]{8,12}$/;

const validateEmail = (raw: string): string => {
  const address = raw.trim();
  if (!address) return "Add an email address.";
  if (address.length > EMAIL_MAX_LENGTH) return "This email address is too long. Check it.";
  if (EMAIL_PATTERN.test(address)) return "";
  return address.includes("@")
    ? `This email address is missing its ending, such as .com. Check ${address}.`
    : `This isn’t an email address. Use the full address, such as ${EXAMPLE_EMAIL}.`;
};

const validateSlack = (raw: string): string => {
  const address = raw.trim();
  if (!address) return `Add a Slack channel, such as ${EXAMPLE_SLACK_CHANNEL}.`;
  return SLACK_NAME_PATTERN.test(address) || SLACK_ID_PATTERN.test(address)
    ? ""
    : `Slack posts go to a channel. Start the address with #, such as ${EXAMPLE_SLACK_CHANNEL}.`;
};

const TEAMS_WEBHOOK_FIX = "Paste the channel’s Workflows webhook link, which starts with https:// and comes from Teams.";

/**
 * "" when `raw` is a Teams webhook address, else why not. Mirrors magic-core `teams_problem`:
 * https, no sign-in part, no port or 443, at most 2048 characters, no spaces inside, no IP
 * address, and a host that ends in one of TEAMS_WEBHOOK_HOSTS.
 */
export const teamsWebhookProblem = (raw: string): string => {
  const address = raw.trim();
  if (!address) return "Add the Teams channel’s webhook link.";
  if (address.length > TEAMS_WEBHOOK_MAX_LENGTH || /\s/.test(address)) return TEAMS_WEBHOOK_FIX;
  let url: URL;
  try {
    url = new URL(address);
  } catch {
    return TEAMS_WEBHOOK_FIX;
  }
  if (url.protocol !== "https:" || url.username || url.password) return TEAMS_WEBHOOK_FIX;
  if (url.port && url.port !== "443") return TEAMS_WEBHOOK_FIX;
  const host = url.hostname.toLowerCase();
  if (host.startsWith("[") || /^[\d.]+$/.test(host)) return TEAMS_WEBHOOK_FIX;
  return TEAMS_WEBHOOK_HOSTS.some((suffix) => host.endsWith(suffix)) ? "" : TEAMS_WEBHOOK_FIX;
};

export const DISTRIBUTION_CHANNELS: Record<ChannelApi, DistributionChannelConfig> = {
  email: {
    id: "email",
    label: "Email",
    icon: "mail",
    placeholder: EXAMPLE_EMAIL,
    hint: "An email address. Files are attached.",
    comingSoon: false,
    validate: validateEmail,
  },
  slack: {
    id: "slack",
    label: "Slack",
    icon: "slack",
    placeholder: EXAMPLE_SLACK_CHANNEL,
    hint: "A Slack channel. The post includes the file.",
    comingSoon: false,
    validate: validateSlack,
  },
  teams: {
    id: "teams",
    label: "Microsoft Teams",
    icon: "teams",
    placeholder: "https://…/workflows/… webhook link",
    hint: "Paste the channel’s Workflows webhook link. The post links to the files.",
    comingSoon: false,
    validate: teamsWebhookProblem,
  },
  link: {
    id: "link",
    label: "Secure link",
    icon: "lock",
    placeholder: EXAMPLE_EMAIL,
    hint: "We email a sign-in link to their copy. No files are attached.",
    comingSoon: true,
    validate: (a) =>
      EMAIL_PATTERN.test(a.trim())
        ? ""
        : `A secure link is sent to an email address. Use the full address, such as ${EXAMPLE_EMAIL}.`,
  },
  drive: {
    id: "drive",
    label: "Google Drive",
    icon: "drive",
    placeholder: "drive.google.com/drive/folders/…",
    hint: "Files are saved to a shared Drive folder. No message is sent.",
    comingSoon: true,
    validate: (a) =>
      /drive\.google\.com\/(drive\/)?folders\/[\w-]{6,}/i.test(a.trim())
        ? ""
        : "Paste the link of a Drive folder, such as drive.google.com/drive/folders/1AbC…",
  },
};

/** Channel order for selects: live ones first, then the coming-soon ones. */
export const CHANNEL_ORDER: readonly ChannelApi[] = ["email", "slack", "teams", "link", "drive"];

/**
 * An address as it is shown in a run. A Teams webhook link carries a secret, so only its host
 * shows, followed by "…". Email and Slack addresses are shown as they are.
 */
export const displayAddress = (channel: ChannelApi, address: string): string => {
  if (channel !== "teams") return address;
  try {
    return `${new URL(address.trim()).hostname}…`;
  } catch {
    return "…";
  }
};

export const isLiveChannel = (channel: ChannelApi): boolean => LIVE_CHANNELS.includes(channel);

/**
 * Reads a channel cell from a CSV ("E-mail", "MS Teams", "gdrive"…); when the cell is blank or
 * unknown, guesses from the address the way the prototype does.
 */
export const parseChannel = (raw: string, address: string): ChannelApi => {
  const key = raw.trim().toLowerCase().replace(/[\s_-]+/g, " ");
  const map: Record<string, ChannelApi> = {
    email: "email",
    "e mail": "email",
    mail: "email",
    slack: "slack",
    teams: "teams",
    "microsoft teams": "teams",
    "ms teams": "teams",
    link: "link",
    "secure link": "link",
    drive: "drive",
    "google drive": "drive",
    gdrive: "drive",
  };
  if (map[key]) return map[key];
  const a = address.trim();
  if (a.startsWith("#")) return "slack";
  if (/drive\.google\.com/i.test(a)) return "drive";
  if (/›|>/.test(a) || teamsWebhookProblem(a) === "") return "teams";
  return "email";
};
